import * as React from 'react';
import { 
  Button,
  Input,
  ScrollArea,
  Badge,
  toast,
  Separator
} from '@/components/ui';
import { Check, X, Search, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { 
  SortDirectionEnum as SortDirection,
  OrderByClause
} from '@/lib/engine';
import { ColumnRef } from '../../../data-grid/types';

interface ColumnSelectorProps {
  originalColumns: ColumnRef[];
  selectedColumns: ColumnRef[];
  orderBy: OrderByClause[];
  onColumnChange: (columns: ColumnRef[]) => void;
  onOrderByChange: (orderBy: OrderByClause[]) => void;
  onSelectedItemClick?: () => void;
}

/**
 * Column Selector Component
 * Allows users to select columns for a query and set sort order
 */
export function ColumnSelector({ 
  originalColumns, 
  selectedColumns, 
  orderBy,
  onColumnChange,
  onOrderByChange,
  onSelectedItemClick
}: ColumnSelectorProps) {
  const [columnFilter, setColumnFilter] = React.useState('');

  // Function to check if a column is selected
  const isColumnSelected = (column: ColumnRef) => {
    return selectedColumns.some((c) => c.name === column.name);
  };
  
  // Function to toggle column selection
  const toggleColumnSelection = (column: ColumnRef, isRemoval = false) => {
    try {
      if (isColumnSelected(column)) {
        // Remove the column
        onColumnChange(selectedColumns.filter((c) => c.name !== column.name));
        
        // Also remove any sort for this column
        const newOrderBy = orderBy.filter(item => item.column.name !== column.name);
        if (newOrderBy.length !== orderBy.length) {
          onOrderByChange(newOrderBy);
        }
      } else {
        // Add the column - use the full column definition from originalColumns
        const originalColumn = originalColumns.find((c) => c.name === column.name);
        if (originalColumn) {
          onColumnChange([...selectedColumns, originalColumn]);
        } else {
          // Fallback to using the provided column if not found in originalColumns
          onColumnChange([...selectedColumns, column]);
        }
      }
      
      // We're removing the call to onSelectedItemClick here to prevent the query pane from closing
      // when selecting columns
    } catch (error) {
      // Show error in toast notification
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'string' 
          ? error 
          : 'An error occurred while toggling column selection';
      
      toast.error(errorMessage, {
        description: 'Please try again with a different column.',
        duration: 5000,
        closeButton: true,
      });
      
      console.error('Error toggling column selection:', error);
    }
  };
  
  // Function to remove a selected column
  const removeSelectedColumn = (column: ColumnRef) => {
    toggleColumnSelection(column, true);
  };

  // Function to toggle sorting for a column
  const toggleSorting = (column: ColumnRef, e: React.MouseEvent) => {
    // Prevent the click from also toggling column selection
    e.stopPropagation();
    
    try {
      // Only allow sorting on selected columns
      if (!isColumnSelected(column)) {
        toast.info("Select the column first to enable sorting", { duration: 2000 });
        return;
      }
      
      // Find if the column is already in the orderBy list
      const existingIndex = orderBy.findIndex(item => item.column.name === column.name);
      
      // Create a new orderBy array - Note: We only support single-column sorting
      let newOrderBy: OrderByClause[];
      
      if (existingIndex === -1) {
        // Column not in orderBy, add it with ASC direction
        // Replace any existing sort with this new one (single-column sorting only)
        newOrderBy = [{
          column,
          direction: SortDirection.ASC
        }];
      } else {
        // Column already in orderBy
        const existingItem = orderBy[existingIndex];
        
        if (existingItem && existingItem.direction === SortDirection.ASC) {
          // Change direction to DESC
          newOrderBy = [{
            column,
            direction: SortDirection.DESC
          }];
        } else {
          // Remove the column from orderBy
          newOrderBy = [];
        }
      }
      
      // Update the orderBy
      onOrderByChange(newOrderBy);
    } catch (error) {
      // Show error in toast notification
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'string' 
          ? error 
          : 'An error occurred while toggling sorting';
      
      toast.error(errorMessage, {
        description: 'Please try again with a different column.',
        duration: 5000,
        closeButton: true,
      });
      
      console.error('Error toggling sorting:', error);
    }
  };

  // Check if a column is already in the sort order and get its direction
  const getColumnSortDirection = (column: ColumnRef) => {
    const sortItem = orderBy.find(item => 
      item.column.name === column.name
    );
    
    return sortItem ? sortItem.direction : null;
  };

  // Filter columns based on search input
  const filteredColumns = React.useMemo(() => {
    if (!columnFilter) return originalColumns;
    
    const lowerFilter = columnFilter.toLowerCase();
    return originalColumns.filter((col) => 
      col.name.toLowerCase().includes(lowerFilter) || 
      col.schema.type.toString().toLowerCase().includes(lowerFilter)
    );
  }, [originalColumns, columnFilter]);

  return (
    <div className="h-full flex flex-col">
      {/* Selected columns section */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-xs font-medium text-muted-foreground">Selected columns</h4>
          <span className="text-xs text-muted-foreground">{selectedColumns.length} selected</span>
        </div>
        <div className="flex flex-wrap gap-1 p-1.5 bg-muted/30 rounded-md border border-border/50 min-h-[3.5rem]">
          {selectedColumns.length === 0 ? (
            <div className="w-full text-center text-xs text-muted-foreground py-2">
              No columns selected. Select columns from the list below.
            </div>
          ) : (
            selectedColumns.map((column, index) => {
              const sortDirection = getColumnSortDirection(column);
              return (
                <Badge 
                  key={index} 
                  variant="outline"
                  className="flex items-center gap-0.5 py-0.5 px-1.5 text-xs bg-muted/30 border-muted-foreground/20 text-muted-foreground hover:bg-muted/50"
                >
                  <span>{column.name}</span>
                  {sortDirection && (
                    <span className="ml-1 text-primary">
                      {sortDirection === SortDirection.ASC ? '↑' : '↓'}
                    </span>
                  )}
                  <button
                    className="ml-1 text-muted-foreground hover:text-foreground focus:outline-none"
                    onClick={() => removeSelectedColumn(column)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })
          )}
        </div>
      </div>
      
      {/* Available columns section with action buttons */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-xs font-medium text-muted-foreground">Available columns</h4>
          <div className="flex items-center gap-1">
            <Button
              variant="link"
              size="sm"
              className="h-5 text-xs p-0"
              onClick={() => onColumnChange(originalColumns)}
            >
              Select All
            </Button>
            <span className="text-xs text-muted-foreground">|</span>
            <Button
              variant="link"
              size="sm"
              className="h-5 text-xs p-0"
              onClick={() => onColumnChange([])}
            >
              Clear All
            </Button>
          </div>
        </div>
        <div className="relative">
          <Input
            placeholder="Search columns..."
            value={columnFilter}
            onChange={(e) => setColumnFilter(e.target.value)}
            className="h-7 text-xs pr-7"
          />
          <Search className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        </div>
      </div>
      
      {/* Sorting hint */}
      <div className="text-xs text-muted-foreground mb-2">
        Only one column can be sorted at a time. Click column to select, click sort icon to toggle: None → Ascending (↑) → Descending (↓) → None
      </div>
      
      {/* Available columns list with sort options */}
      <ScrollArea className="flex-1 border rounded-md">
        <div className="divide-y">
          {filteredColumns.map((column, index) => {
            const isSelected = isColumnSelected(column);
            const sortDirection = getColumnSortDirection(column);
            
            return (
              <div 
                key={index} 
                className="flex items-center justify-between py-1.5 px-2 hover:bg-muted/50 transition-colors"
                onClick={() => toggleColumnSelection(column)}
              >
                <div className="flex items-center gap-2 cursor-pointer">
                  <div className={`w-4 h-4 flex items-center justify-center rounded-sm border ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                    {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <div className="text-sm">{column.name}</div>
                  <span className="text-xs text-muted-foreground">
                    {column.schema.type.toString()}
                  </span>
                </div>
                
                <div 
                  className={`flex items-center justify-center w-6 h-6 rounded-sm cursor-pointer ${isSelected ? (sortDirection ? 'text-primary' : 'text-muted-foreground') : 'text-muted-foreground/30'}`}
                  onClick={(e) => toggleSorting(column, e)}
                >
                  {sortDirection === SortDirection.ASC ? (
                    <ArrowUp className="h-4 w-4" />
                  ) : sortDirection === SortDirection.DESC ? (
                    <ArrowDown className="h-4 w-4" />
                  ) : (
                    <ArrowUpDown className="h-4 w-4 opacity-50" />
                  )}
                </div>
              </div>
            );
          })}
          
          {filteredColumns.length === 0 && (
            <div className="text-center p-4 text-sm text-muted-foreground">
              No columns match your search
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
} 